import React, { useEffect } from 'react';
import { SignUp, useUser } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';
import TroubleSigningIn from '../components/TroubleSigningIn';
import { track } from '../lib/ga';

const SignUpPage: React.FC = () => {
  const { isLoaded, isSignedIn } = useUser();

  // Honour ?redirect_url (same-origin paths only) so a visitor who came for a
  // specific page — e.g. the Funeral Guide — lands back on it after creating
  // their account. New accounts without an intent default to onboarding.
  const params = new URLSearchParams(window.location.search);
  const rawRedirect = params.get('redirect_url') || '';
  const redirectTo = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : '/onboarding';

  useEffect(() => {
    document.title = 'Cyrano - Eulogy Generator';
    track('sign_up_view');
    // Scroll to top on page load
    window.scrollTo(0, 0);
    console.log('🔍 SignUp page mounted - URL:', window.location.href);
    console.log('🔍 User signed in:', isSignedIn);
    return () => {
      console.log('🔍 SignUp page unmounting');
    };
  }, [isSignedIn]);

  // Already signed in → honour the intent if one was carried, else dashboard
  if (isLoaded && isSignedIn) {
    console.log('🔍 User already signed in, redirecting');
    return <Navigate to={rawRedirect ? redirectTo : '/dashboard'} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-warm-background px-4 py-8">
      <div className="w-full max-w-[520px]">
        {/* Logo Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img 
              src="/Assets/GhostQuillLogo.png" 
              alt="Cyrano" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-serif text-[2.5rem] font-semibold text-space-cadet">
              Cyrano
            </span>
          </div>
          <p className="text-cool-gray text-base italic">Eulogy Assistant</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-5 sm:p-8 md:p-10 shadow-[0_8px_32px_rgba(25,40,92,0.12)] border border-space-cadet/8">
          {/* Welcome Text */}
          <div className="mb-8 text-left">
            <h1 className="font-serif text-[1.5rem] sm:text-[2rem] font-semibold text-space-cadet mb-2">
              Create Your Account
            </h1>
            <p className="text-cool-gray text-base leading-relaxed text-left">
              Get started with Cyrano, preserve your treasured memories and honor your loved ones
            </p>
          </div>

          {/* Clerk SignUp Component */}
          <SignUp
            appearance={{
              elements: {
                rootBox: 'w-full',
                card: 'shadow-none border-0 p-0',
                headerTitle: 'hidden',
                headerSubtitle: 'hidden',
                socialButtonsBlockButton: 'w-full py-3.5 px-4 border-2 border-cool-gray/20 rounded-lg bg-white text-elegant-black hover:border-space-cadet hover:bg-space-cadet/[0.02] transition-all flex items-center justify-center gap-3 text-[0.95rem] font-medium mb-3',
                socialButtonsBlockButtonText: 'text-elegant-black',
                dividerLine: 'bg-cool-gray/20',
                dividerText: 'text-cool-gray text-sm',
                formFieldLabel: 'text-space-cadet font-medium text-[0.95rem] mb-2',
                formFieldInput: 'w-full py-3.5 px-4 border-2 border-cool-gray/20 rounded-lg text-base text-elegant-black focus:border-space-cadet focus:ring-0 focus:ring-offset-0 focus:shadow-[0_0_0_3px_rgba(25,40,92,0.1)]',
                formButtonPrimary: 'w-full py-4 bg-space-cadet hover:bg-space-cadet/90 text-white rounded-lg font-medium text-base transition-all hover:translate-y-[-1px] hover:shadow-[0_4px_12px_rgba(25,40,92,0.2)]',
                footerActionLink: 'text-space-cadet hover:text-space-cadet/80 font-medium',
                formFieldInputShowPasswordButton: 'text-space-cadet hover:text-space-cadet-2',
                identityPreviewText: 'text-space-cadet',
                identityPreviewEditButton: 'text-space-cadet hover:text-space-cadet/80',
                formFieldSuccessText: 'text-space-cadet',
                formFieldErrorText: 'text-red-600',
              },
              variables: {
                colorPrimary: '#19285c',
                colorText: '#060001',
                colorTextSecondary: '#8c83b4',
                borderRadius: '0.5rem',
              }
            }}
            signInUrl={rawRedirect ? `/sign-in?redirect_url=${encodeURIComponent(redirectTo)}` : '/sign-in'}
            afterSignUpUrl={redirectTo}
            redirectUrl={redirectTo}
          />

          <TroubleSigningIn />

          {/* Back Link */}
          <div className="text-center mt-3">
            <a
              href="/landing.html"
              className="text-cool-gray text-sm inline-flex items-center gap-2 hover:text-space-cadet transition-colors"
            >
              ← Back to home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
